const SCROLL_ANIMATION_CONFIGURATION = {
  mobileBreakpoint: '(max-width: 989px)',
  colourChangeEvent: 'kb:pen-colour-change',
  defaultAnimationKey: 'champagne',
  textCrossfadeDurationSeconds: 0.5,
  textCrossfadeEase: 'power2.out',
  minimumSeekDifferenceSeconds: 0.025,
};

class ScrollAnimation extends HTMLElement {
  #abortController = null;
  #animations = [];
  #activeAnimation = null;
  #scrollTrigger = null;
  #gsap = null;
  #mobileMediaQuery = null;
  #currentProgress = 0;
  #activeStoryIndex = -1;
  #seekAnimationFrame = null;
  #pendingSeekProgress = 0;
  #loadRequestId = 0;

  async connectedCallback() {
    this.#abortController = new AbortController();

    this.#animations = Array.from(
      this.querySelectorAll('[data-scroll-animation-source]')
    );

    if (this.#animations.length === 0) return;

    this.#mobileMediaQuery = window.matchMedia(
      SCROLL_ANIMATION_CONFIGURATION.mobileBreakpoint
    );

    this.#setInitialAnimation();

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    this.#setUpColourListener();
    this.#setUpViewportListener();

    await this.#loadAnimation(this.#activeAnimation, {
      progress: 0,
      activate: true,
    });

    if (this.#abortController.signal.aborted) return;

    if (prefersReducedMotion) {
      this.#applyReducedMotionState();
      return;
    }

    const [{ gsap }, { ScrollTrigger }] = await Promise.all([
      import('@theme/gsap'),
      import('@theme/scrolltrigger'),
    ]);

    if (this.#abortController.signal.aborted) return;

    this.#gsap = gsap;

    this.#setUpScrollAnimation(gsap, ScrollTrigger);
  }

  disconnectedCallback() {
    this.#abortController?.abort();

    this.#scrollTrigger?.kill();
    this.#scrollTrigger = null;

    if (this.#seekAnimationFrame) {
      cancelAnimationFrame(this.#seekAnimationFrame);
      this.#seekAnimationFrame = null;
    }

    this.#mobileMediaQuery?.removeEventListener(
      'change',
      this.#handleViewportChange
    );
  }

  #setInitialAnimation() {
    const sharedColour =
      window.KBPenColour?.get()?.colorHandle ||
      document.documentElement.getAttribute(
        'data-kb-pen-colour'
      );

    const defaultKey =
      sharedColour ||
      this.dataset.defaultColour ||
      SCROLL_ANIMATION_CONFIGURATION.defaultAnimationKey;

    const defaultAnimation = this.#animations.find(
      (animation) =>
        animation.dataset.animationKey?.toLowerCase() ===
        defaultKey.toLowerCase()
    );

    this.#activeAnimation = defaultAnimation ?? this.#animations[0];

    this.#animations.forEach((animation) => {
      const isActive = animation === this.#activeAnimation;

      animation.classList.toggle('is-active', isActive);
      animation.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.#setActiveStory(0, {
      animate: false,
      animation: this.#activeAnimation,
    });
  }

  #setUpScrollAnimation(gsap, ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.config({
      ignoreMobileResize: true,
      autoRefreshEvents: 'visibilitychange,DOMContentLoaded,resize',
    });

    const track = this.querySelector('.scroll-animation__track');

    if (!track) return;

    this.#scrollTrigger = ScrollTrigger.create({
      trigger: track,
      start: 'top top',
      end: 'bottom bottom',
      scrub: true,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        this.#handleProgress(self.progress);
      },
    });

    this.#handleProgress(this.#scrollTrigger.progress);
  }

  #handleProgress(progress) {
    const normalizedProgress = this.#clampProgress(progress);

    this.#currentProgress = normalizedProgress;

    this.#queueVideoSeek(normalizedProgress);
    this.#updateStory(normalizedProgress);
  }

  #clampProgress(progress) {
    return Math.min(1, Math.max(0, progress));
  }

  #queueVideoSeek(progress) {
    this.#pendingSeekProgress = progress;

    if (this.#seekAnimationFrame) return;

    this.#seekAnimationFrame = requestAnimationFrame(() => {
      this.#seekAnimationFrame = null;
      this.#seekActiveVideo();
    });
  }

  #seekActiveVideo() {
    const video = this.#getVideo(this.#activeAnimation);

    if (!video) return;
    if (!Number.isFinite(video.duration) || video.duration <= 0) return;
    if (video.seeking) return;

    this.#seekVideo(video, this.#pendingSeekProgress);
  }

  #seekVideo(video, progress) {
    if (!video) return;
    if (!Number.isFinite(video.duration) || video.duration <= 0) return;

    const maxTime = Math.max(0, video.duration - 0.01);

    const targetTime =
      progress >= 1
        ? maxTime
        : Math.min(maxTime, progress * video.duration);

    if (
      Math.abs(video.currentTime - targetTime) <
      SCROLL_ANIMATION_CONFIGURATION.minimumSeekDifferenceSeconds
    ) {
      return;
    }

    try {
      video.currentTime = targetTime;
    } catch (error) {
      // Ignore transient seek errors while video initializes.
    }
  }

  #setUpVideoSeekListener(video) {
    if (!video || video.dataset.seekListenerReady === 'true') return;

    video.dataset.seekListenerReady = 'true';

    video.addEventListener(
      'seeked',
      () => {
        this.#queueVideoSeek(this.#pendingSeekProgress);
      },
      {
        signal: this.#abortController.signal,
      }
    );
  }

  #updateStory(progress) {
    if (!this.#activeAnimation) return;

    const stories = this.#getStories(this.#activeAnimation);

    if (stories.length === 0) return;

    const enterPoints = this.#getStoryEnterPoints(stories);
    const storyIndex = this.#getStoryIndex(progress, enterPoints);

    if (storyIndex === this.#activeStoryIndex) return;

    this.#setActiveStory(storyIndex, {
      animate: true,
      animation: this.#activeAnimation,
    });
  }

  #getStories(animation) {
    if (!animation) return [];

    return Array.from(
      animation.querySelectorAll('[data-scroll-animation-story]')
    );
  }

  #getStoryEnterPoints(stories) {
    const storyCount = stories.length;

    return stories.map((story, index) => {
      const authoredEnterPoint = Number(story.dataset.enterAt);

      if (Number.isFinite(authoredEnterPoint)) {
        return this.#clampProgress(authoredEnterPoint / 100);
      }

      return index / storyCount;
    });
  }

  #getStoryIndex(progress, enterPoints) {
    let activeIndex = 0;

    for (let index = 0; index < enterPoints.length; index += 1) {
      if (progress >= enterPoints[index]) {
        activeIndex = index;
      } else {
        break;
      }
    }

    return activeIndex;
  }

  #setActiveStory(
    storyIndex,
    {
      animate = true,
      animation = this.#activeAnimation,
    } = {}
  ) {
    if (!animation) return;

    const stories = this.#getStories(animation);

    if (stories.length === 0) {
      this.#activeStoryIndex = -1;
      return;
    }

    const nextIndex = Math.min(
      stories.length - 1,
      Math.max(0, storyIndex)
    );

    this.#activeStoryIndex = nextIndex;

    stories.forEach((story, index) => {
      const isActive = index === nextIndex;

      story.classList.toggle('is-active', isActive);

      if (!this.#gsap || !animate) {
        story.style.opacity = isActive ? '1' : '0';
        story.style.visibility = isActive ? 'visible' : 'hidden';
        story.style.transform = '';

        return;
      }

      this.#gsap.killTweensOf(story);

      this.#gsap.to(story, {
        autoAlpha: isActive ? 1 : 0,
        duration:
          SCROLL_ANIMATION_CONFIGURATION.textCrossfadeDurationSeconds,
        ease:
          SCROLL_ANIMATION_CONFIGURATION.textCrossfadeEase,
        overwrite: 'auto',
      });
    });
  }

  async #setAnimation(animationKey) {
    if (!animationKey) return;

    const normalizedKey = animationKey
      .toString()
      .trim()
      .toLowerCase();

    const nextAnimation = this.#animations.find(
      (animation) =>
        animation.dataset.animationKey?.toLowerCase() === normalizedKey
    );

    if (!nextAnimation) return;
    if (nextAnimation === this.#activeAnimation) return;

    const previousAnimation = this.#activeAnimation;
    const progress = this.#currentProgress;

    const loaded = await this.#loadAnimation(nextAnimation, {
      progress,
      activate: false,
    });

    if (!loaded) return;
    if (this.#abortController?.signal.aborted) return;

    this.#activeAnimation = nextAnimation;
    this.#activeStoryIndex = -1;

    previousAnimation?.classList.remove('is-active');
    previousAnimation?.setAttribute('aria-hidden', 'true');

    nextAnimation.classList.add('is-active');
    nextAnimation.setAttribute('aria-hidden', 'false');

    this.#updateStory(progress);
    this.#queueVideoSeek(progress);
  }

  async #loadAnimation(
    animation,
    {
      progress = this.#currentProgress,
      activate = false,
    } = {}
  ) {
    if (!animation) return false;

    const requestId = ++this.#loadRequestId;

    const video = this.#getVideo(animation);
    const source = this.#getVideoSource(animation);

    if (!video || !source) return false;

    this.#setUpVideoSeekListener(video);

    if (video.dataset.loadedSource === source) {
      this.#seekVideo(video, progress);

      if (activate) {
        animation.classList.add('is-active');
        animation.setAttribute('aria-hidden', 'false');
      }

      return true;
    }

    video.pause();

    video.src = source;
    video.dataset.loadedSource = source;
    video.load();

    const loaded = await this.#waitForVideoMetadata(video);

    if (!loaded) return false;
    if (requestId !== this.#loadRequestId) return false;

    this.#seekVideo(video, progress);

    if (activate) {
      animation.classList.add('is-active');
      animation.setAttribute('aria-hidden', 'false');
    }

    return true;
  }

  #getVideo(animation) {
    return animation?.querySelector('.scroll-animation__video') ?? null;
  }

  #getVideoSource(animation) {
    if (!animation) return '';

    const isMobile = this.#mobileMediaQuery?.matches;

    if (isMobile) {
      return (
        animation.dataset.videoMobile ||
        animation.dataset.videoDesktop ||
        ''
      );
    }

    return (
      animation.dataset.videoDesktop ||
      animation.dataset.videoMobile ||
      ''
    );
  }

  #waitForVideoMetadata(video) {
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const handleLoaded = () => {
        cleanup();
        resolve(true);
      };

      const handleError = () => {
        cleanup();
        resolve(false);
      };

      const handleAbort = () => {
        cleanup();
        resolve(false);
      };

      const cleanup = () => {
        video.removeEventListener('loadedmetadata', handleLoaded);
        video.removeEventListener('error', handleError);

        this.#abortController?.signal.removeEventListener(
          'abort',
          handleAbort
        );
      };

      video.addEventListener('loadedmetadata', handleLoaded, {
        once: true,
      });

      video.addEventListener('error', handleError, {
        once: true,
      });

      this.#abortController?.signal.addEventListener(
        'abort',
        handleAbort,
        {
          once: true,
        }
      );
    });
  }

  #setUpColourListener() {
    document.addEventListener(
      SCROLL_ANIMATION_CONFIGURATION.colourChangeEvent,
      this.#handleColourChange,
      {
        signal: this.#abortController.signal,
      }
    );
  }

  #handleColourChange = (event) => {
    const colourHandle = event.detail?.colorHandle;

    if (!colourHandle) return;

    this.#setAnimation(colourHandle);
  };

  #setUpViewportListener() {
    this.#mobileMediaQuery?.addEventListener(
      'change',
      this.#handleViewportChange
    );
  }

  #handleViewportChange = async () => {
    if (!this.#activeAnimation) return;

    await this.#loadAnimation(this.#activeAnimation, {
      progress: this.#currentProgress,
      activate: true,
    });

    this.#scrollTrigger?.refresh();
    this.#queueVideoSeek(this.#currentProgress);
  };

  #applyReducedMotionState() {
    this.#currentProgress = 0;

    this.#seekActiveVideo();

    this.#setActiveStory(0, {
      animate: false,
      animation: this.#activeAnimation,
    });
  }
}

if (!customElements.get('scroll-animation')) {
  customElements.define('scroll-animation', ScrollAnimation);
}

export { ScrollAnimation };